/**
 * Protected route component for admin-only pages
 * Requires Clerk authentication and admin role
 */

import { useHasRole, type UserRole } from "@/lib/auth";
import { getClerkPublishableKey } from "@/lib/clerkPublishableKey";
import { RedirectToSignIn, useAuth, useUser } from "@clerk/clerk-react";
import { Navigate } from "react-router-dom";

type ProtectedAdminRouteProps = {
  children: React.ReactNode;
  title?: string; // Optional title for backwards compatibility
  /** Minimum role required. Defaults to "admin". Pass "super_admin" for sensitive pages. */
  requireRole?: UserRole;
};

/**
 * Inner component — only rendered when ClerkProvider is in context.
 * All hooks live here so they are always called unconditionally (Rules of Hooks).
 */
function ProtectedAdminRouteContent({
  children,
  requireRole,
}: {
  children: React.ReactNode;
  requireRole: UserRole;
}) {
  const { isLoaded, isSignedIn } = useAuth();
  const { isLoaded: userLoaded } = useUser();
  const hasRequiredRole = useHasRole(requireRole);

  if (!isLoaded) {
    return (
      <div className="font-ios min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div className="font-ios min-h-dvh min-h-screen bg-background">
        <RedirectToSignIn />
      </div>
    );
  }

  if (!userLoaded) {
    return (
      <div className="font-ios min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando perfil...</div>
      </div>
    );
  }

  if (!hasRequiredRole) {
    return <Navigate to={requireRole === "super_admin" ? "/admin" : "/"} replace />;
  }

  return <div className="font-ios min-h-full min-w-0">{children}</div>;
}

/**
 * Protects admin routes — requires Clerk authentication and admin role.
 *
 * Outer shell: guards against a missing Clerk publishable key (build-time var).
 * When the key is present, delegates to ProtectedAdminRouteContent which holds
 * all hooks so they are always called in the same order (no conditional hook calls).
 */
export function ProtectedAdminRoute({ children, requireRole = "admin" }: ProtectedAdminRouteProps) {
  if (!getClerkPublishableKey()) {
    return (
      <div className="font-ios min-h-screen flex flex-col items-center justify-center gap-3 bg-background p-6 text-center">
        <p className="text-lg font-semibold text-foreground">Admin: Clerk no configurado en el build</p>
        <p className="text-sm text-muted-foreground max-w-md">
          Define <code className="rounded bg-muted px-1">VITE_CLERK_PUBLISHABLE_KEY</code> o{" "}
          <code className="rounded bg-muted px-1">NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY</code> y volvé a
          desplegar.
        </p>
      </div>
    );
  }

  return <ProtectedAdminRouteContent requireRole={requireRole}>{children}</ProtectedAdminRouteContent>;
}

// Default export for backwards compatibility
export default ProtectedAdminRoute;
